import { useState } from 'react'
import { X } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AutocompleteCustomerContainer } from '@/components/AutocompleteCustomerContainer'
import { RichMarkdownEditor } from '@/components/RichMarkdownEditor'
import { type SearchOption } from '@/components/ui/Search'
import { UnderlineDateInput } from '@/components/ui/underline-date-input'
import { UnderlineInput } from '@/components/ui/underline-input'
import {
  addInterval,
  describeSchedule,
  describeStep,
  isDateStep,
  stepDueAt,
  type IntervalStep,
  type IntervalUnit,
  type NewFollowUpInput,
  type ScheduleStep,
} from '@/db/domain/followUps'
import { fromZaLocalToUtcIso, toZaDateKey, toZaNowDateKey } from '@/lib/datetime'

export interface NewFollowUpDialogProps {
  /** Pre-selected customer, when raising a follow-up from their page. */
  initialCustomer?: SearchOption | null
  /** Staff who can be assigned; omitting hides the assignee picker. */
  assigneeOptions?: Array<{ id: string; name: string }>
  /** Curated categories (PocketBase-managed); omitting hides the picker. */
  categoryOptions?: Array<{ id: string; name: string }>
  /**
   * Create a brand-new customer from the search box (name + phone). Present
   * means the dialog offers "create" in the customer search; the follow-up
   * then attaches to the customer this returns.
   */
  onCreateCustomer?: (input: {
    fullName: string
    contactPhone?: string
    contactEmail?: string
  }) => Promise<{ id: string }> | { id: string }
  /** 'edit' prefills from initialValues, locks the customer, and saves. */
  mode?: 'create' | 'edit'
  /** Prefill for edit mode. `schedule` is the *remaining* ladder. */
  initialValues?: {
    title: string
    /** Current due day (ZA date key) — the dialog opens in manual-date mode. */
    dueDay: string
    schedule: ScheduleStep[]
    assigneeId?: string
    categoryId?: string
  }
  onCancel: () => void
  /**
   * Deliver the follow-up (create) or its replacement values (edit).
   * `extras.firstNote` is markdown for an opening note on the customer
   * thread — create mode only.
   */
  onCreate: (input: NewFollowUpInput, extras: { firstNote?: string }) => void
}

/**
 * How the first due date is being chosen.
 *
 * `expression` derives it from a sentence — "[2] [weeks] [before] [date]" —
 * which covers both "in a week" (after today) and lead times ("2 weeks
 * before the warranty ends"). `date` is the manual override: the computed
 * date field is directly editable, and editing it makes the explicit date
 * the source of truth.
 */
type ReminderMode = 'expression' | 'date'

interface ReminderExpression {
  amount: number
  unit: IntervalUnit
  direction: 'after' | 'before'
  /** ZA-local date key the expression measures from. */
  anchor: string
}

/**
 * Raising a follow-up.
 *
 * The first due date reads as a sentence — "[2] [weeks] [before|after]
 * [date]" — because both of the ways staff think about reminders fit it:
 * "in a week" is 1 week after today, and "call before the warranty runs
 * out" is 2 weeks before its end date. Quick chips fill the sentence; the
 * computed date is shown and directly editable as the manual override. The
 * repeat ladder below uses the same select controls, with each rung showing
 * the date it would land on.
 */
export function NewFollowUpDialog({
  initialCustomer = null,
  assigneeOptions,
  categoryOptions,
  onCreateCustomer,
  mode = 'create',
  initialValues,
  onCancel,
  onCreate,
}: NewFollowUpDialogProps) {
  const isEdit = mode === 'edit'
  const [selectedCustomer, setSelectedCustomer] = useState<SearchOption | null>(
    initialCustomer,
  )
  const [title, setTitle] = useState(initialValues?.title ?? '')
  // Defaults to Today in manual-date mode — the expression row is one tap
  // away for anything fancier.
  const [reminderMode, setReminderMode] = useState<ReminderMode>('date')
  const [expr, setExpr] = useState<ReminderExpression>({
    amount: 1,
    unit: 'week',
    direction: 'after',
    anchor: toZaNowDateKey(),
  })
  const [explicitDay, setExplicitDay] = useState(
    initialValues?.dueDay ?? toZaNowDateKey(),
  )
  const [assigneeId, setAssigneeId] = useState(initialValues?.assigneeId ?? '')
  const [categoryId, setCategoryId] = useState(initialValues?.categoryId ?? '')
  const [newCustomerPhone, setNewCustomerPhone] = useState('')
  const [newCustomerEmail, setNewCustomerEmail] = useState('')
  // Each entry renders as an editable row — the selects/date input in a row
  // edit the ladder directly, so there is no separate "draft" state to
  // confuse with the real thing. "+ Add repeat" appends a row.
  const [schedule, setSchedule] = useState<ScheduleStep[]>(
    initialValues?.schedule ?? [],
  )
  // Markdown for the opening note on the customer thread (create only).
  // Collapsed behind an Add note button — the composer takes real space.
  const [showFirstNote, setShowFirstNote] = useState(false)
  const [firstNote, setFirstNote] = useState('')

  const customerId =
    selectedCustomer && !selectedCustomer.isNew && selectedCustomer.id != null
      ? String(selectedCustomer.id)
      : ''
  const isNewCustomer = Boolean(
    selectedCustomer?.isNew && selectedCustomer.name.trim() && onCreateCustomer,
  )

  const todayKey = toZaNowDateKey()

  const exprDueAt = expr.anchor
    ? addInterval(fromZaLocalToUtcIso(expr.anchor, '08:00'), {
        amount: expr.direction === 'before' ? -expr.amount : expr.amount,
        unit: expr.unit,
      })
    : ''
  const dueAt =
    reminderMode === 'date'
      ? explicitDay
        ? fromZaLocalToUtcIso(explicitDay, '08:00')
        : ''
      : exprDueAt
  const dueKey = dueAt ? toZaDateKey(dueAt) : ''

  const setExpression = (next: Partial<ReminderExpression>) => {
    setReminderMode('expression')
    setExpr((current) => ({ ...current, ...next }))
  }

  const pickExplicitDay = (day: string) => {
    setReminderMode('date')
    setExplicitDay(day)
  }

  // A date row being composed has an empty onDate until picked — it renders,
  // but stays out of the summary, the projections, and what gets saved.
  const effectiveSchedule = schedule.filter(
    (rung) => !isDateStep(rung) || rung.onDate,
  )

  // Where each ladder rung would land, walking from the first due date.
  // Approximate for interval rungs (a real `done` measures from completion),
  // exact for date rungs — either way it makes the ladder concrete. Indexed
  // to match `schedule`; incomplete rows project '' and don't advance.
  const projectedDates: string[] = []
  {
    let cursor = dueAt
    for (const rung of schedule) {
      if (isDateStep(rung) && !rung.onDate) {
        projectedDates.push('')
        continue
      }
      cursor = cursor ? stepDueAt(cursor, rung) : ''
      projectedDates.push(cursor)
    }
  }

  const updateRung = (index: number, rung: ScheduleStep) => {
    setSchedule((current) =>
      current.map((step, i) => (i === index ? rung : step)),
    )
  }

  const canSubmit = Boolean(
    (customerId || isNewCustomer) && title.trim() && dueAt,
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = async () => {
    if (!canSubmit || isSubmitting) return
    setIsSubmitting(true)
    try {
      // A follow-up can be the very first record of a customer — a walk-in
      // who left a name and number. The customer is created (and *persisted*:
      // follow_ups.customer_id is a relation, so the server must know the
      // customer before it will accept the follow-up) and then followed up.
      const resolvedCustomerId = isNewCustomer
        ? (
            await Promise.resolve(
              onCreateCustomer!({
                fullName: selectedCustomer!.name.trim(),
                contactPhone: newCustomerPhone.trim() || undefined,
                contactEmail: newCustomerEmail.trim() || undefined,
              }),
            )
          ).id
        : customerId
      // A lead-time reminder should explain itself when it fires months from
      // now, so the anchor it counts back from rides along in the details
      // (the details *field* left this dialog, but the domain field stays).
      const beforeNote =
        reminderMode === 'expression' &&
        expr.direction === 'before' &&
        expr.anchor
          ? `Reminding ${expr.amount} ${unitLabel(expr.unit, expr.amount)} before ${expr.anchor}.`
          : ''
      onCreate(
        {
          customerId: resolvedCustomerId,
          title: title.trim(),
          details: beforeNote || undefined,
          dueAt,
          schedule: effectiveSchedule,
          assigneeId: assigneeId || undefined,
          categoryId: categoryId || undefined,
        },
        {
          firstNote:
            !isEdit && showFirstNote && firstNote.trim() ? firstNote : undefined,
        },
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const exprRowDimmed = reminderMode === 'date' ? 'opacity-60' : ''

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onCancel())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit follow-up' : 'New follow-up'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Change what it says, when it reminds, and how it repeats.'
              : 'A reminder to get back to a customer.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <span className={fieldLabelClass}>Customer</span>
            <AutocompleteCustomerContainer
              value={selectedCustomer}
              onChange={setSelectedCustomer}
              ariaLabel="Search customers"
              allowCreateOption={Boolean(onCreateCustomer)}
              disabled={isEdit}
            />
          </div>

          {isNewCustomer ? (
            <div className="space-y-1">
              <span className={fieldLabelClass}>
                Contact for {selectedCustomer!.name.trim()}
              </span>
              <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                <UnderlineInput
                  value={newCustomerPhone}
                  type="tel"
                  aria-label="New customer phone"
                  placeholder="Phone"
                  onChange={(event) => setNewCustomerPhone(event.target.value)}
                />
                <UnderlineInput
                  value={newCustomerEmail}
                  type="email"
                  aria-label="New customer email"
                  placeholder="Email"
                  onChange={(event) => setNewCustomerEmail(event.target.value)}
                />
              </div>
              <p className="text-xs text-slate-500">
                Optional — phone and/or email, so they can be reached.
              </p>
            </div>
          ) : null}

          <div className="space-y-1">
            <span className={fieldLabelClass}>What needs following up</span>
            <UnderlineInput
              value={title}
              aria-label="What needs following up"
              placeholder="e.g. Quote for the replacement SSD"
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          {!isEdit ? (
            showFirstNote ? (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className={fieldLabelClass}>First note</span>
                  <button
                    type="button"
                    className="cursor-pointer rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                    aria-label="Remove note"
                    onClick={() => {
                      setShowFirstNote(false)
                      setFirstNote('')
                    }}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
                <RichMarkdownEditor
                  initialValue=""
                  onChange={setFirstNote}
                  placeholder="Starts the customer thread"
                  ariaLabel="First note"
                  autoFocus
                />
              </div>
            ) : (
              <div>
                <button
                  type="button"
                  className={chipClass}
                  onClick={() => setShowFirstNote(true)}
                >
                  + Add note
                </button>
              </div>
            )
          ) : null}

          {categoryOptions?.length ? (
            <div className="space-y-1">
              <span className={fieldLabelClass}>Category</span>
              <select
                className="h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-[13px] text-slate-700"
                value={categoryId}
                aria-label="Category"
                onChange={(event) => setCategoryId(event.target.value)}
              >
                <option value="">No category</option>
                {categoryOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {assigneeOptions?.length ? (
            <div className="space-y-1">
              <span className={fieldLabelClass}>Assign to</span>
              <select
                className="h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-[13px] text-slate-700"
                value={assigneeId}
                aria-label="Assign to"
                onChange={(event) => setAssigneeId(event.target.value)}
              >
                <option value="">Unassigned</option>
                {assigneeOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <fieldset className="space-y-3">
            <legend className={fieldLabelClass}>First reminder</legend>

            {/* Quick fills — each one just sets the sentence below. */}
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                className={
                  reminderMode === 'date' && explicitDay === todayKey
                    ? chipSelectedClass
                    : chipClass
                }
                onClick={() => pickExplicitDay(todayKey)}
              >
                Today
              </button>
              <button
                type="button"
                className={
                  reminderMode === 'expression' &&
                  expressionMatches(expr, 1, 'day', todayKey)
                    ? chipSelectedClass
                    : chipClass
                }
                onClick={() =>
                  setExpression({
                    amount: 1,
                    unit: 'day',
                    direction: 'after',
                    anchor: todayKey,
                  })
                }
              >
                Tomorrow
              </button>
              <button
                type="button"
                className={
                  reminderMode === 'date' &&
                  explicitDay === nextMondayKey() &&
                  explicitDay !== todayKey
                    ? chipSelectedClass
                    : chipClass
                }
                onClick={() => pickExplicitDay(nextMondayKey())}
              >
                Next Monday
              </button>
              {quickIntervals.map((step) => (
                <button
                  key={`${step.amount}-${step.unit}`}
                  type="button"
                  className={
                    reminderMode === 'expression' &&
                    expressionMatches(expr, step.amount, step.unit, todayKey)
                      ? chipSelectedClass
                      : chipClass
                  }
                  onClick={() =>
                    setExpression({
                      amount: step.amount,
                      unit: step.unit,
                      direction: 'after',
                      anchor: todayKey,
                    })
                  }
                >
                  {describeStep(step)}
                </button>
              ))}
            </div>

            {/* The sentence: [2] [weeks] [before|after] [anchor] → [due]. */}
            <div
              className={`flex flex-wrap items-center gap-x-1.5 gap-y-2.5 ${exprRowDimmed}`}
            >
              <select
                className={selectClass}
                value={expr.amount}
                aria-label="Reminder amount"
                onChange={(event) =>
                  setExpression({ amount: Number(event.target.value) })
                }
              >
                {AMOUNT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <select
                className={selectClass}
                value={expr.unit}
                aria-label="Reminder unit"
                onChange={(event) =>
                  setExpression({ unit: event.target.value as IntervalUnit })
                }
              >
                {UNIT_VALUES.map((unit) => (
                  <option key={unit} value={unit}>
                    {unitLabel(unit, expr.amount)}
                  </option>
                ))}
              </select>
              <select
                className={selectClass}
                value={expr.direction}
                aria-label="Before or after"
                onChange={(event) =>
                  setExpression({
                    direction: event.target.value as 'after' | 'before',
                  })
                }
              >
                <option value="after">after</option>
                <option value="before">before</option>
              </select>
              <UnderlineDateInput
                className="w-[10rem]"
                inputClassName="font-medium text-slate-900"
                value={expr.anchor}
                aria-label="Anchor date"
                onChange={(event) =>
                  setExpression({ anchor: event.target.value })
                }
              />
            </div>

            {/* The consequence — editable; editing makes the date the truth. */}
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2.5">
              <span className="text-sm text-slate-500">Due</span>
              <UnderlineDateInput
                className="w-[10rem]"
                inputClassName={
                  reminderMode === 'date'
                    ? 'font-semibold text-slate-900'
                    : 'text-slate-700'
                }
                value={reminderMode === 'date' ? explicitDay : dueKey}
                aria-label="Due date"
                onChange={(event) => pickExplicitDay(event.target.value)}
              />
              {reminderMode === 'expression' &&
              expr.direction === 'before' &&
              expr.anchor ? (
                <span className="text-sm text-slate-500">
                  — {expr.amount} {unitLabel(expr.unit, expr.amount)} before{' '}
                  {expr.anchor}
                </span>
              ) : null}
              {dueKey && dueKey < todayKey ? (
                <span className="text-sm font-bold text-amber-600">
                  — already past, due immediately
                </span>
              ) : null}
            </div>
            {dueKey && dueKey >= todayKey ? (
              <p className="text-sm font-bold text-slate-800">
                {describeDistanceFromToday(dueKey, todayKey)}
              </p>
            ) : null}
          </fieldset>

          <fieldset className="space-y-1.5">
            <legend className={fieldLabelClass}>Then repeat</legend>
            {schedule.length ? (
              <ul className="space-y-1.5">
                {schedule.map((rung, index) => (
                  /** biome-ignore lint/suspicious/noArrayIndexKey: rows are edited in place, never reordered **/
                  <li key={index} className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm text-slate-500">then</span>
                    <select
                      className={selectClass}
                      value={isDateStep(rung) ? 'date' : 'interval'}
                      aria-label={`Repeat ${index + 1} kind`}
                      onChange={(event) =>
                        updateRung(
                          index,
                          event.target.value === 'date'
                            ? { onDate: '' }
                            : { amount: 1, unit: 'year' },
                        )
                      }
                    >
                      <option value="interval">in</option>
                      <option value="date">on</option>
                    </select>
                    {isDateStep(rung) ? (
                      <UnderlineDateInput
                        className="w-[10rem]"
                        value={rung.onDate}
                        aria-label={`Repeat ${index + 1} date`}
                        onChange={(event) =>
                          updateRung(index, { onDate: event.target.value })
                        }
                      />
                    ) : (
                      <>
                        <select
                          className={selectClass}
                          value={rung.amount}
                          aria-label={`Repeat ${index + 1} amount`}
                          onChange={(event) =>
                            updateRung(index, {
                              ...rung,
                              amount: Number(event.target.value),
                            })
                          }
                        >
                          {AMOUNT_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                        <select
                          className={selectClass}
                          value={rung.unit}
                          aria-label={`Repeat ${index + 1} unit`}
                          onChange={(event) =>
                            updateRung(index, {
                              ...rung,
                              unit: event.target.value as IntervalUnit,
                            })
                          }
                        >
                          {UNIT_VALUES.map((unit) => (
                            <option key={unit} value={unit}>
                              {unitLabel(unit, rung.amount)}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                    {projectedDates[index] ? (
                      <span className="text-sm text-slate-400">
                        → {toZaDateKey(projectedDates[index])}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className="cursor-pointer rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                      aria-label={`Remove repeat ${index + 1}`}
                      onClick={() =>
                        setSchedule((current) =>
                          current.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                className={schedule.length === 0 ? chipSelectedClass : chipClass}
                onClick={() => setSchedule([])}
              >
                No repeat
              </button>
              <button
                type="button"
                className={chipClass}
                onClick={() =>
                  setSchedule((current) => [
                    ...current,
                    { amount: 1, unit: 'year' },
                  ])
                }
              >
                + Add repeat
              </button>
            </div>

            <p className="text-sm text-slate-600">
              Reminds {describeSchedule(effectiveSchedule)}
              {effectiveSchedule.length
                ? ' (after the first one).'
                : ' — marking it done will finish it.'}
            </p>
          </fieldset>
        </div>

        <DialogFooter>
          <button type="button" className={cancelButtonClass} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={
              canSubmit ? confirmButtonClass : `${confirmButtonClass} opacity-50`
            }
            disabled={!canSubmit}
            onClick={submit}
          >
            {isEdit ? 'Save changes' : 'Create follow-up'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * 'That's about 11 months from today.' — a plain-language sanity check on
 * the computed due date, so a mis-set anchor or direction is caught before
 * saving rather than when the reminder fails to fire.
 */
function describeDistanceFromToday(dueKey: string, todayKey: string): string {
  const days = Math.round(
    (Date.parse(`${dueKey}T00:00:00Z`) - Date.parse(`${todayKey}T00:00:00Z`)) /
      86_400_000,
  )
  if (days === 0) return "That's today."
  if (days === 1) return "That's tomorrow."
  if (days < 21) return `That's ${days} days from today.`
  if (days < 70) return `That's about ${Math.round(days / 7)} weeks from today.`
  if (days < 700) {
    return `That's about ${Math.round(days / 30.44)} months from today.`
  }
  const years = days / 365.25
  const rounded = Math.round(years * 2) / 2
  return `That's about ${rounded} years from today.`
}

/** Quick-fill chips: the intervals staff reach for, all "after today". */
const quickIntervals: IntervalStep[] = [
  { amount: 1, unit: 'week' },
  { amount: 2, unit: 'week' },
  { amount: 1, unit: 'month' },
  { amount: 6, unit: 'month' },
  { amount: 1, unit: 'year' },
]

const AMOUNT_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1)
const UNIT_VALUES: IntervalUnit[] = ['day', 'week', 'month', 'year']

function unitLabel(unit: IntervalUnit, amount: number): string {
  return amount === 1 ? unit : `${unit}s`
}

function expressionMatches(
  expr: ReminderExpression,
  amount: number,
  unit: IntervalUnit,
  todayKey: string,
): boolean {
  return (
    expr.amount === amount &&
    expr.unit === unit &&
    expr.direction === 'after' &&
    expr.anchor === todayKey
  )
}

/** ZA-local date key of the coming Monday — a week out when today is Monday. */
function nextMondayKey(): string {
  const todayKey = toZaDateKey(new Date().toISOString())
  const day = new Date(`${todayKey}T00:00:00Z`)
  const delta = (8 - day.getUTCDay()) % 7 || 7
  day.setUTCDate(day.getUTCDate() + delta)
  return day.toISOString().slice(0, 10)
}

const fieldLabelClass =
  'text-xs font-semibold uppercase tracking-wide text-slate-500'
const chipClass =
  'rounded-full border border-slate-300 bg-white px-3 py-1 text-sm text-slate-600 hover:bg-slate-50'
const chipSelectedClass =
  'rounded-full border border-blue-400 bg-blue-500 px-3 py-1 text-sm font-medium text-white'
const selectClass =
  'h-8 cursor-pointer rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700'
const cancelButtonClass =
  'inline-flex h-9 items-center rounded-lg border border-slate-300 bg-white px-3 text-[13px] text-slate-600 hover:bg-slate-50'
const confirmButtonClass =
  'inline-flex h-9 items-center rounded-lg border border-blue-400 bg-blue-500 px-3 text-[13px] font-medium text-white hover:bg-blue-600'
